import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonOff as DeactivateIcon,
  PersonAdd as ActivateIcon,
  People as PeopleIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  ForwardToInbox as ResendIcon,
} from '@mui/icons-material';
import { usersAPI, customersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  const [customers, setCustomers] = useState([]);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'user',
    customer_id: '',
    is_active: true,
  });

  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  useEffect(() => {
    customersAPI.getAll()
      .then((res) => setCustomers(res.data))
      .catch(() => {});
  }, []);

  const invitationExpired = (user) =>
    !user.activation_expires_at || new Date(user.activation_expires_at) < new Date();

  const handleResendInvitation = async (user) => {
    setError('');
    setSuccess('');
    try {
      await usersAPI.resendInvitation(user.id);
      setSuccess(`Neue Einladung an ${user.email} verschickt (14 Tage gültig)`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Einladung konnte nicht verschickt werden');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await usersAPI.getAll({ include_inactive: includeInactive });
      setUsers(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Benutzer');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = { ...formData };

      // Client users must be tied to a customer
      if (data.role === 'client' && !data.customer_id) {
        setError('Bitte einen Kunden für den externen Benutzer auswählen');
        return;
      }
      // Only client users carry a customer link
      if (data.role !== 'client') {
        data.customer_id = null;
      }

      // Don't send password if editing and password field is empty
      if (editingId && !data.password) {
        delete data.password;
      }

      if (editingId) {
        await usersAPI.update(editingId, data);
        setSuccess('Benutzer erfolgreich aktualisiert');
      } else {
        await usersAPI.create(data);
        setSuccess('Benutzer erfolgreich erstellt');
      }

      resetForm();
      fetchUsers();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Benutzers');
    }
  };

  const handleEdit = (user) => {
    setFormData({
      username: user.username,
      email: user.email || '',
      full_name: user.full_name || '',
      password: '',
      role: user.role,
      customer_id: user.customer_id || '',
      is_active: user.is_active,
    });
    setEditingId(user.id);
    setShowModal(true);
  };

  const handleDeactivate = async (id, currentStatus) => {
    const action = currentStatus ? 'deaktivieren' : 'aktivieren';
    if (!window.confirm(`Möchten Sie diesen Benutzer wirklich ${action}?`)) {
      return;
    }

    try {
      await usersAPI.update(id, { is_active: !currentStatus });
      setSuccess(`Benutzer erfolgreich ${action}t`);
      fetchUsers();
    } catch (err) {
      setError(`Fehler beim ${action} des Benutzers`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Benutzer wirklich dauerhaft löschen?')) {
      return;
    }

    try {
      await usersAPI.delete(id);
      setSuccess('Benutzer erfolgreich gelöscht');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen des Benutzers');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: 'user',
      customer_id: '',
      is_active: true,
    });
    setEditingId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (currentUser.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <BlockIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: '#e01e26' }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Zugriff verweigert
              </Typography>
              <Typography variant="body1" color="error.main">
                Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Admin-Rechte erforderlich.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700}>
          Benutzerverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateModal}
          sx={{
            background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
            },
          }}
        >
          Benutzer erstellen
        </Button>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              Alle Benutzer
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)}
                  sx={{
                    color: '#1e3a5f',
                    '&.Mui-checked': {
                      color: '#1e3a5f',
                    },
                  }}
                />
              }
              label="Deaktivierte anzeigen"
            />
          </Box>

          {users.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <PeopleIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Keine Benutzer gefunden
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Benutzername</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Vollständiger Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>E-Mail</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rolle</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        bgcolor: !user.is_active ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                        textDecoration: !user.is_active ? 'line-through' : 'none',
                        opacity: !user.is_active ? 0.6 : 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {user.username}
                          </Typography>
                          {user.id === currentUser.id && (
                            <Box
                              sx={{
                                display: 'inline-block',
                                px: 1,
                                py: 0.25,
                                bgcolor: '#f5f5f5',
                                borderRadius: 0.5,
                                border: '1px solid #e0e0e0',
                              }}
                            >
                              <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.7rem' }}>
                                Sie
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.full_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            bgcolor: '#f5f5f5',
                            borderRadius: 0.5,
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#424242', fontSize: '0.875rem' }}>
                            {user.role === 'admin'
                              ? 'Administrator'
                              : user.role === 'client'
                                ? `Kunde${user.customer_name ? ` · ${user.customer_name}` : ''}`
                                : 'Benutzer'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            bgcolor: user.is_active ? 'rgba(16,185,129,0.1)' : '#f5f5f5',
                            borderRadius: 0.5,
                            border: '1px solid',
                            borderColor: user.is_active ? 'rgba(16,185,129,0.4)' : '#e0e0e0',
                          }}
                        >
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: user.is_active ? '#10b981' : '#9e9e9e' }} />
                          <Typography variant="body2" sx={{ color: user.is_active ? '#0f8a63' : '#757575', fontSize: '0.875rem', fontWeight: 600 }}>
                            {user.is_active ? 'Aktiv' : 'Inaktiv'}
                          </Typography>
                        </Box>
                        {user.is_active && user.invitation_pending && (
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.5 }}>
                            {invitationExpired(user) ? 'Einladung abgelaufen' : 'Eingeladen, noch nicht aktiviert'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(user)}
                            sx={{
                              color: '#1e3a5f',
                              '&:hover': { bgcolor: 'primary.light' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {user.is_active && user.invitation_pending && (
                            <IconButton
                              size="small"
                              title="Einladung erneut senden"
                              onClick={() => handleResendInvitation(user)}
                              sx={{ color: '#1e3a5f', '&:hover': { bgcolor: 'primary.light' } }}
                            >
                              <ResendIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            title={user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                            onClick={() => handleDeactivate(user.id, user.is_active)}
                            sx={{
                              color: user.is_active ? '#dc3545' : '#10b981',
                              '&:hover': { bgcolor: user.is_active ? 'error.light' : 'success.light' },
                            }}
                          >
                            {user.is_active ? <DeactivateIcon fontSize="small" /> : <ActivateIcon fontSize="small" />}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(user.id)}
                            disabled={user.id === currentUser.id}
                            sx={{
                              color: '#e01e26',
                              '&:hover': { bgcolor: 'error.light' },
                              '&.Mui-disabled': {
                                color: 'action.disabled',
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {editingId ? 'Benutzer bearbeiten' : 'Benutzer erstellen'}
          </Typography>
          <IconButton onClick={closeModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="user-form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Benutzername"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="username"
            />

            <TextField
              fullWidth
              margin="normal"
              label="E-Mail-Adresse"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Vollständiger Name"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              autoComplete="name"
            />

            <TextField
              fullWidth
              margin="normal"
              label={editingId ? 'Passwort (leer lassen, um beizubehalten)' : 'Passwort (optional)'}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required={false}
              helperText={!editingId ? 'Wenn leer gelassen, erhält der Benutzer eine Aktivierungs-E-Mail' : ''}
              autoComplete="new-password"
            />

            <TextField
              select
              fullWidth
              margin="normal"
              label="Rolle"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
            >
              <MenuItem value="user">Benutzer</MenuItem>
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="client">Kunde (extern)</MenuItem>
            </TextField>

            {formData.role === 'client' && (
              <TextField
                select
                fullWidth
                margin="normal"
                label="Kunde"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                helperText="Der externe Benutzer sieht ausschließlich Projekte dieses Kunden"
              >
                {customers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.company_name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.is_active}
                  onChange={handleChange}
                  name="is_active"
                  sx={{
                    color: '#1e3a5f',
                    '&.Mui-checked': {
                      color: '#1e3a5f',
                    },
                  }}
                />
              }
              label="Aktiv (Häkchen entfernen = deaktivieren)"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="user-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            {editingId ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UsersPage;
